import type { BrowseSeoPageModel } from '../../shared/pages/pageModels';
import type { PublicTopic } from './browseTypes';

const MODEL_ID = 'browse-page-model';

/**
 * Returns the server catalog page only for a clean, explicit `?page=N`
 * request. Filter/search variants remain ordinary noindex SPA Browse routes.
 */
export function readBrowseSeoPageModel(doc: Document = document): BrowseSeoPageModel | null {
	if (typeof window === 'undefined' || window.location.pathname.replace(/\/+$/u, '') !== '/browse') return null;
	const params = new URLSearchParams(window.location.search);
	const values = params.getAll('page');
	if (params.size !== 1 || values.length !== 1 || !/^\d+$/u.test(values[0])) return null;
	const requestedPage = Number(values[0]);
	if (!Number.isSafeInteger(requestedPage) || requestedPage < 1) return null;
	const raw = doc.getElementById(MODEL_ID)?.textContent;
	if (!raw) return null;
	try {
		const parsed: unknown = JSON.parse(raw);
		if (!isBrowseSeoPageModel(parsed)) return null;
		if (parsed.page !== requestedPage || parsed.canonicalPath !== (requestedPage >= 2 ? `/browse?page=${requestedPage}` : '/browse')) return null;
		if (parsed.lastPage !== null && parsed.page > parsed.lastPage) return null;
		return parsed;
	} catch {
		return null;
	}
}

export function browseSeoTopics(model: BrowseSeoPageModel): PublicTopic[] {
	return model.topics;
}

function isBrowseSeoPageModel(value: unknown): value is BrowseSeoPageModel {
	if (!value || typeof value !== 'object') return false;
	const model = value as Partial<BrowseSeoPageModel>;
	const lastPage = model.lastPage;
	return model.pageType === 'browse-seo'
		&& Number.isSafeInteger(model.page) && (model.page ?? 0) >= 1
		&& (lastPage === null || (Number.isSafeInteger(lastPage) && (lastPage ?? 0) >= 1))
		&& typeof model.canonicalPath === 'string'
		&& typeof model.robots === 'string'
		&& model.isParameterVariant === false
		&& Array.isArray(model.topics)
		&& model.topics.every((topic) => Boolean(topic) && typeof topic.id === 'string' && typeof topic.slug === 'string' && typeof topic.title === 'string'
			&& typeof topic.subtitle === 'string' && typeof topic.category === 'string' && typeof topic.source === 'string'
			&& Number.isSafeInteger(topic.chapterCount) && Number.isFinite(topic.audioMinutes));
}
